import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { OnlyMeToggle, usePrivatePages } from '../lib/privatePages';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { VenueOutreachScreen } from './VenueOutreachScreen';

/**
 * GROWTH HQ — Evan's growth back end (growth-agent handoff, 2026-10-12).
 *
 * One pipeline for venues AND adjacent companies AND people, on top of the existing outreach
 * machine: `outreach_targets` carries the new columns (target_kind/stage/assignee/priority/
 * pinned/next_step), and everything goes through the admin_growth_* definer RPCs because the
 * outreach tables deliberately have no RLS (see growth_hq_v1.sql).
 *
 * The full venue CRM + email machine + inbox is the existing VenueOutreachScreen, mounted whole
 * under the "Venues & email" tab — it moved here from the sidebar's Venue Portal slot.
 * Phone-usable on purpose: the pipeline renders as cards, not a wide table.
 */

const STAGES = ['discovered', 'contacted', 'meeting', 'agreed', 'live', 'active', 'churned'] as const;
type Stage = (typeof STAGES)[number];

type PipelineRow = {
  id: string;
  kind: 'venue' | 'company' | 'person';
  name: string;
  org_type: string | null;
  city: string | null;
  stage: Stage;
  status: string;
  assignee: string | null;
  priority: number;
  pinned: boolean;
  next_step: string | null;
  next_step_due: string | null;
  notes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_instagram: string | null;
  venue_id: string | null;
  score: number | null;
  raters: number | null;
  sent_at: string | null;
  replied_at: string | null;
  updated_at: string;
};

type TeamMember = { id: number; name: string; city: string | null; focus: string | null; active: boolean };
type Activity = { id: number; target_id: string | null; actor: string; note: string; created_at: string };
type CityStat = {
  city: string;
  venues_live: number;
  tents: number;
  scans: number;
  signups: number;
  activated: number;
  rated_venues: number;
};

const STAGE_COLORS: Record<Stage, string> = {
  discovered: 'border-border text-mutedDim',
  contacted: 'border-sky-500/45 bg-sky-500/10 text-sky-300',
  meeting: 'border-amber-400/45 bg-amber-400/10 text-amber-300',
  agreed: 'border-accent/45 bg-accent/10 text-accent',
  live: 'border-emerald-500/45 bg-emerald-500/10 text-emerald-300',
  active: 'border-emerald-500/60 bg-emerald-500/20 text-emerald-200',
  churned: 'border-red-500/45 bg-red-500/10 text-red-300',
};

export function GrowthHQScreen() {
  const [tab, setTab] = useState<'portal' | 'contacts' | 'team' | 'cities'>('portal');
  const [rows, setRows] = useState<PipelineRow[] | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [cityStats, setCityStats] = useState<CityStat[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [kindFilter, setKindFilter] = useState<'all' | 'company' | 'person'>('all');
  const [cityFilter, setCityFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [touchNote, setTouchNote] = useState('');
  // Team is editable only by the master admin (Evan) — enforced by RLS + is_master_admin(); this
  // just decides whether to draw the edit controls (Evan 2026-11-02).
  const { isMaster, isHidden, isPrivate } = usePrivatePages();

  const load = useCallback(async () => {
    setError(null);
    const [pipe, tm, act] = await Promise.all([
      supabase.rpc('admin_growth_pipeline'),
      supabase.from('growth_team_members').select('*').order('name'),
      supabase.from('growth_activities').select('*').order('id', { ascending: false }).limit(200),
    ]);
    if (pipe.error) {
      setError(
        /admin_growth_pipeline/.test(pipe.error.message)
          ? 'Growth HQ needs SQL applied: shado-admin/supabase/sql/growth_hq_v1.sql'
          : pipe.error.message,
      );
      setRows([]);
      return;
    }
    setRows((pipe.data ?? []) as PipelineRow[]);
    setTeam(((tm.data ?? []) as TeamMember[]).filter((m) => m.active));
    setActivities((act.data ?? []) as Activity[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab !== 'cities' || cityStats !== null) return;
    void supabase.rpc('admin_growth_city_stats').then(({ data, error: e }) => {
      if (e) setError(e.message);
      setCityStats((data ?? []) as CityStat[]);
    });
  }, [tab, cityStats]);

  const patch = useCallback(
    async (id: string, p: Record<string, unknown>) => {
      setRows((rs) => (rs ?? []).map((r) => (r.id === id ? ({ ...r, ...p } as PipelineRow) : r)));
      const { error: e } = await supabase.rpc('admin_growth_update_target', { p_id: id, p_patch: p });
      if (e) {
        setError(e.message);
        void load();
      }
    },
    [load],
  );

  const logTouch = useCallback(
    async (targetId: string | null, note: string, actor = 'Evan') => {
      if (!note.trim()) return;
      const { error: e } = await supabase
        .from('growth_activities')
        .insert({ target_id: targetId, actor, note: note.trim() })
        .select('id');
      if (e) {
        setError(e.message);
        return;
      }
      setTouchNote('');
      const { data } = await supabase
        .from('growth_activities')
        .select('*')
        .order('id', { ascending: false })
        .limit(200);
      setActivities((data ?? []) as Activity[]);
    },
    [],
  );

  const filtered = useMemo(() => {
    const list = (rows ?? []).filter((r) => {
      // Contacts = people and companies (the Jug manager, formals CEOs, promoters).
      // Venue relationships live in the Venue Portal tab's CRM.
      if (r.kind === 'venue') return false;
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (cityFilter && !(r.city ?? '').toLowerCase().includes(cityFilter.toLowerCase())) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      if (a.priority !== b.priority) return b.priority - a.priority;
      return (b.raters ?? -1) - (a.raters ?? -1);
    });
  }, [rows, kindFilter, cityFilter]);

  const weekAgo = Date.now() - 7 * 86400000;
  const touchesThisWeek = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of activities) {
      if (Date.parse(a.created_at) < weekAgo) continue;
      m.set(a.actor, (m.get(a.actor) ?? 0) + 1);
    }
    return m;
  }, [activities, weekAgo]);

  return (
    <div className="admin-page mx-auto max-w-none">
      <PageHeader
        title="Growth HQ"
        subtitle="Venue Portal = the venue CRM + email machine. Contacts = the people and companies around it."
      />

      <div className="mb-5 flex items-end gap-0.5 overflow-x-auto border-b border-borderStrong">
        {(
          [
            ['portal', 'Venue Portal'],
            ['contacts', 'Contacts'],
            ['team', 'Team'],
            ['cities', 'Cities'],
          ] as const
        )
          // A tab the master admin made private vanishes for everyone else (Evan 2026-11-02).
          .filter(([id]) => !isHidden(`/growth#${id}`))
          .map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 pb-2.5 pt-1 text-[12.5px] font-medium transition-colors ${
              tab === id ? 'border-accent text-white' : 'border-transparent text-mutedDim hover:text-white'
            }`}
          >
            {label}
            {isMaster && isPrivate(`/growth#${id}`) ? <span className="ml-1 text-[9px] text-accent">●</span> : null}
          </button>
        ))}
        <div className="ml-auto pb-2">
          <OnlyMeToggle pageKey={`/growth#${tab}`} />
        </div>
      </div>

      {error ? <div className="admin-banner-error mb-4">{error}</div> : null}

      {tab === 'portal' ? <VenueOutreachScreen /> : null}

      {tab === 'contacts' ? (
        <div className="mx-auto max-w-4xl">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {(
              [
                ['all', 'All'],
                ['company', 'Companies'],
                ['person', 'People'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-active={kindFilter === k}
                onClick={() => setKindFilter(k)}
                className="admin-pill"
              >
                {label}
              </button>
            ))}
            <input
              className="admin-input !w-[150px] !text-[12px]"
              placeholder="city…"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setAddOpen((o) => !o)}
              className="admin-btn admin-btn-secondary admin-btn-sm ml-auto"
            >
              {addOpen ? 'Close' : '+ Company / person'}
            </button>
          </div>

          {addOpen ? <AddTarget team={team} onAdded={() => { setAddOpen(false); void load(); }} /> : null}

          {rows === null ? (
            <SkeletonRows rows={8} />
          ) : filtered.length === 0 ? (
            <EmptyState title="No contacts yet" hint="Add the people and companies you talk to — bar managers, formals companies, promoters — with + Company / person." />
          ) : (
            <div className="space-y-2">
              {filtered.map((r) => {
                const open = openId === r.id;
                return (
                  <div key={r.id} className="admin-card !p-0">
                    <button type="button" onClick={() => setOpenId(open ? null : r.id)} className="block w-full px-4 py-2.5 text-left">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void patch(r.id, { pinned: !r.pinned });
                          }}
                          title={r.pinned ? 'Unpin' : 'Pin to top'}
                          className={`shrink-0 text-[15px] ${r.pinned ? 'text-amber-300' : 'text-mutedDim hover:text-amber-300'}`}
                        >
                          {r.pinned ? '★' : '☆'}
                        </button>
                        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-white">{r.name}</span>
                        <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAGE_COLORS[r.stage] ?? STAGE_COLORS.discovered}`}>
                          {r.stage}
                        </span>
                        {r.kind !== 'venue' ? (
                          <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] text-mutedDim">
                            {r.org_type || r.kind}
                          </span>
                        ) : null}
                        <span className="shrink-0 text-[11px] text-mutedDim">{r.city ?? '—'}</span>
                        {r.kind === 'venue' ? (
                          <span className="shrink-0 font-display text-[12.5px] font-semibold tabular-nums text-[#b79cff]">
                            {r.score != null ? Number(r.score).toFixed(1) : '—'}
                            <span className="ml-1 font-sans text-[10px] font-normal text-mutedDim">{r.raters ?? 0} raters</span>
                          </span>
                        ) : null}
                        {r.assignee ? <span className="shrink-0 text-[10.5px] text-sky-300">{r.assignee}</span> : null}
                        {r.contact_email ? <span className="shrink-0 text-[10.5px] text-emerald-300">✉</span> : null}
                        {r.contact_phone ? <span className="shrink-0 text-[10.5px] text-emerald-300">☎</span> : null}
                        {r.contact_instagram ? <span className="shrink-0 text-[10.5px] text-emerald-300">IG</span> : null}
                      </div>
                      {r.next_step ? (
                        <div className="mt-1 pl-7 text-[11.5px] text-muted">
                          → {r.next_step}
                          {r.next_step_due ? <span className="text-mutedDim"> · by {r.next_step_due}</span> : null}
                        </div>
                      ) : null}
                    </button>

                    {open ? (
                      <div className="space-y-3 border-t border-border/60 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            className="admin-input !w-auto !px-2 !text-[12px]"
                            value={r.stage}
                            onChange={(e) => void patch(r.id, { stage: e.target.value })}
                          >
                            {STAGES.map((st) => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                          <select
                            className="admin-input !w-auto !px-2 !text-[12px]"
                            value={r.assignee ?? ''}
                            onChange={(e) => void patch(r.id, { assignee: e.target.value })}
                          >
                            <option value="">unassigned</option>
                            {team.map((m) => (
                              <option key={m.id} value={m.name}>{m.name}</option>
                            ))}
                          </select>
                          <label className="flex items-center gap-1 text-[11px] text-mutedDim">
                            priority
                            <input
                              type="number"
                              className="admin-input !w-[64px] !px-1.5 !text-[12px]"
                              value={r.priority}
                              onChange={(e) => void patch(r.id, { priority: Math.round(Number(e.target.value) || 0) })}
                            />
                          </label>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="next step…"
                            defaultValue={r.next_step ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.next_step ?? '')) void patch(r.id, { next_step: e.target.value });
                            }}
                          />
                          <input
                            type="date"
                            className="admin-input !w-auto !text-[12px] [color-scheme:dark]"
                            defaultValue={r.next_step_due ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.next_step_due ?? '')) void patch(r.id, { next_step_due: e.target.value });
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="contact name"
                            defaultValue={r.contact_name ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_name ?? '')) void patch(r.id, { contact_name: e.target.value });
                            }}
                          />
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="contact email"
                            defaultValue={r.contact_email ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_email ?? '')) void patch(r.id, { contact_email: e.target.value });
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="phone"
                            defaultValue={r.contact_phone ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_phone ?? '')) void patch(r.id, { contact_phone: e.target.value });
                            }}
                          />
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="instagram @handle"
                            defaultValue={r.contact_instagram ?? ''}
                            onBlur={(e) => {
                              if (e.target.value !== (r.contact_instagram ?? '')) void patch(r.id, { contact_instagram: e.target.value });
                            }}
                          />
                        </div>
                        <textarea
                          rows={2}
                          className="admin-input !h-auto w-full resize-y !py-2 !text-[12px]"
                          placeholder="notes…"
                          defaultValue={r.notes ?? ''}
                          onBlur={(e) => {
                            if (e.target.value !== (r.notes ?? '')) void patch(r.id, { notes: e.target.value });
                          }}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            className="admin-input flex-1 !text-[12px]"
                            placeholder="log a touch — call, DM, walk-in…"
                            value={openId === r.id ? touchNote : ''}
                            onChange={(e) => setTouchNote(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void logTouch(r.id, touchNote);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => void logTouch(r.id, touchNote)}
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                          >
                            Log
                          </button>
                        </div>
                        <div className="space-y-1">
                          {activities
                            .filter((a) => a.target_id === r.id)
                            .slice(0, 6)
                            .map((a) => (
                              <div key={a.id} className="text-[11px] text-mutedDim">
                                <span className="text-muted">{a.actor}</span> · {a.note} ·{' '}
                                {timeAgo(a.created_at)}
                              </div>
                            ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {tab === 'cities' ? (
        <div className="mx-auto max-w-4xl">
          {cityStats === null ? (
            <SkeletonRows rows={6} />
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table admin-table--compact">
                <thead>
                  <tr>
                    <th>City</th>
                    <th className="num">Venues live</th>
                    <th className="num">Tents</th>
                    <th className="num">Scans</th>
                    <th className="num">Signups</th>
                    <th className="num">Activated</th>
                    <th className="num">Rated venues</th>
                  </tr>
                </thead>
                <tbody>
                  {cityStats.map((c) => (
                    <tr key={c.city}>
                      <td className="key">{c.city}</td>
                      <td className="num">{c.venues_live}</td>
                      <td className="num">{c.tents}</td>
                      <td className="num">{c.scans}</td>
                      <td className="num">{c.signups}</td>
                      <td className="num">{c.activated}</td>
                      <td className="num">{c.rated_venues}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 max-w-2xl text-[11px] leading-relaxed text-mutedDim">
                Venues live = active approved offers. Signups/activated match on the profile&rsquo;s home
                city, so they undercount students whose hometown differs — tent-scan attribution is the
                real fix and counts here the moment scans happen.
              </p>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'team' && !isHidden('/growth#team') ? (
        <div className="mx-auto max-w-3xl space-y-2">
          {team.map((m) =>
            isMaster ? (
              <TeamRowEditor
                key={m.id}
                m={m}
                touches={touchesThisWeek.get(m.name) ?? 0}
                onSaved={() => void load()}
                onError={setError}
              />
            ) : (
              <div key={m.id} className="admin-card flex flex-wrap items-center gap-3 !py-3">
                <span className="text-[13.5px] font-medium text-white">{m.name}</span>
                <span className="text-[11px] text-mutedDim">{m.city ?? '—'}</span>
                <span className="min-w-0 flex-1 truncate text-[11.5px] text-muted">{m.focus ?? ''}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-sky-300">
                  {touchesThisWeek.get(m.name) ?? 0} touches this week
                </span>
              </div>
            ),
          )}
          {isMaster ? <AddTeamMember onAdded={() => void load()} onError={setError} /> : null}
          <div className="admin-card">
            <div className="admin-eyebrow mb-2">This week</div>
            {activities.filter((a) => Date.parse(a.created_at) >= weekAgo).length === 0 ? (
              <p className="text-[11.5px] text-mutedDim">No touches logged yet — log them from any pipeline row.</p>
            ) : (
              <div className="space-y-1">
                {activities
                  .filter((a) => Date.parse(a.created_at) >= weekAgo)
                  .slice(0, 30)
                  .map((a) => (
                    <div key={a.id} className="text-[11.5px] text-muted">
                      <span className="text-white">{a.actor}</span> · {a.note}{' '}
                      <span className="text-mutedDim">· {timeAgo(a.created_at)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AddTarget({ team, onAdded }: { team: TeamMember[]; onAdded: () => void }) {
  const [kind, setKind] = useState<'company' | 'person'>('company');
  const [name, setName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [city, setCity] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactInstagram, setContactInstagram] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [assignee, setAssignee] = useState('Evan');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="admin-card mb-4 space-y-2">
      {err ? <div className="admin-banner-error">{err}</div> : null}
      <div className="flex flex-wrap items-center gap-2">
        {(['company', 'person'] as const).map((k) => (
          <button key={k} type="button" data-active={kind === k} onClick={() => setKind(k)} className="admin-pill">
            {k}
          </button>
        ))}
        <input className="admin-input flex-1 !text-[12px]" placeholder={kind === 'company' ? 'company name' : 'person'} value={name} onChange={(e) => setName(e.target.value)} />
        <input className="admin-input !w-[150px] !text-[12px]" placeholder="type (formals, brand…)" value={orgType} onChange={(e) => setOrgType(e.target.value)} />
        <input className="admin-input !w-[130px] !text-[12px]" placeholder="city" value={city} onChange={(e) => setCity(e.target.value)} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className="admin-input flex-1 !text-[12px]" placeholder="contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        <input className="admin-input flex-1 !text-[12px]" placeholder="contact email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        <input className="admin-input !w-[140px] !text-[12px]" placeholder="phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        <input className="admin-input !w-[150px] !text-[12px]" placeholder="instagram" value={contactInstagram} onChange={(e) => setContactInstagram(e.target.value)} />
        <select className="admin-input !w-auto !px-2 !text-[12px]" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          {team.map((m) => (
            <option key={m.id} value={m.name}>{m.name}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className="admin-input flex-1 !text-[12px]" placeholder="next step" value={nextStep} onChange={(e) => setNextStep(e.target.value)} />
        <button
          type="button"
          disabled={busy || !name.trim()}
          onClick={() => {
            setBusy(true);
            setErr(null);
            void supabase
              .rpc('admin_growth_add_target', {
                p_kind: kind,
                p_name: name.trim(),
                p_org_type: orgType.trim() || null,
                p_city: city.trim() || null,
                p_contact_name: contactName.trim() || null,
                p_contact_email: contactEmail.trim() || null,
                p_next_step: nextStep.trim() || null,
                p_assignee: assignee || null,
                p_contact_phone: contactPhone.trim() || null,
                p_contact_instagram: contactInstagram.trim() || null,
              })
              .then(({ error }) => {
                setBusy(false);
                if (error) setErr(error.message);
                else onAdded();
              });
          }}
          className="admin-btn admin-btn-primary admin-btn-sm"
        >
          {busy ? 'Adding…' : 'Add to pipeline'}
        </button>
      </div>
    </div>
  );
}

/** Inline-editable team row (master admin only). Name goes through master_rename_team_member so
 *  logged touches and pipeline assignments follow the rename; city/focus are plain updates. */
function TeamRowEditor({
  m,
  touches,
  onSaved,
  onError,
}: {
  m: TeamMember;
  touches: number;
  onSaved: () => void;
  onError: (e: string | null) => void;
}) {
  const [name, setName] = useState(m.name);
  const [city, setCity] = useState(m.city ?? '');
  const [focus, setFocus] = useState(m.focus ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(m.name);
    setCity(m.city ?? '');
    setFocus(m.focus ?? '');
  }, [m.name, m.city, m.focus]);
  const dirty = name.trim() !== m.name || city.trim() !== (m.city ?? '') || focus.trim() !== (m.focus ?? '');

  const save = async () => {
    if (!name.trim()) return onError('Name is required');
    setBusy(true);
    onError(null);
    if (name.trim() !== m.name) {
      const { error } = await supabase.rpc('master_rename_team_member', { p_id: m.id, p_name: name.trim() });
      if (error) {
        setBusy(false);
        return onError(/duplicate key/.test(error.message) ? `There's already a team member named ${name.trim()}` : error.message);
      }
    }
    const { error } = await supabase
      .from('growth_team_members')
      .update({ city: city.trim() || null, focus: focus.trim() || null })
      .eq('id', m.id)
      .select('id');
    setBusy(false);
    if (error) return onError(error.message);
    onSaved();
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${m.name} from the team? Their logged touches stay in the history.`)) return;
    setBusy(true);
    const { error } = await supabase.from('growth_team_members').update({ active: false }).eq('id', m.id).select('id');
    setBusy(false);
    if (error) return onError(error.message);
    onSaved();
  };

  const field = 'admin-input !py-1.5 !text-[12.5px]';
  return (
    <div className="admin-card flex flex-wrap items-center gap-2 !py-3">
      <input className={`${field} !w-[130px] font-medium`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
      <input className={`${field} !w-[130px]`} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
      <input className={`${field} min-w-[180px] flex-1`} value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus" />
      <span className="shrink-0 text-[11px] tabular-nums text-sky-300">{touches} touches this week</span>
      {dirty ? (
        <button type="button" disabled={busy} onClick={() => void save()} className="rounded-lg bg-accent px-3 py-1.5 text-[12px] font-semibold text-black disabled:opacity-50">
          Save
        </button>
      ) : null}
      <button type="button" disabled={busy} onClick={() => void remove()} className="text-[11.5px] text-red-400 hover:underline disabled:opacity-50">
        Remove
      </button>
    </div>
  );
}

function AddTeamMember({ onAdded, onError }: { onAdded: () => void; onError: (e: string | null) => void }) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [focus, setFocus] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const n = name.trim();
    if (!n) return onError('Name is required');
    setBusy(true);
    onError(null);
    // A previously removed member with the same name comes back instead of colliding.
    const { data: existing } = await supabase.from('growth_team_members').select('id').eq('name', n).maybeSingle();
    const { error } = existing
      ? await supabase
          .from('growth_team_members')
          .update({ active: true, city: city.trim() || null, focus: focus.trim() || null })
          .eq('id', (existing as { id: number }).id)
      : await supabase.from('growth_team_members').insert({ name: n, city: city.trim() || null, focus: focus.trim() || null });
    setBusy(false);
    if (error) return onError(error.message);
    setName('');
    setCity('');
    setFocus('');
    onAdded();
  };
  const field = 'admin-input !py-1.5 !text-[12.5px]';
  return (
    <div className="admin-card flex flex-wrap items-center gap-2 !py-3">
      <input className={`${field} !w-[130px]`} value={name} onChange={(e) => setName(e.target.value)} placeholder="New member" />
      <input className={`${field} !w-[130px]`} value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
      <input className={`${field} min-w-[180px] flex-1`} value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus" />
      <button type="button" disabled={busy || !name.trim()} onClick={() => void add()} className="rounded-lg bg-accent px-3 py-1.5 text-[12px] font-semibold text-black disabled:opacity-50">
        Add
      </button>
    </div>
  );
}
